/**
 * Paginator — measure, column map, page count, goTo.
 *
 * design.md §5.1. One CSS multi-column track per chapter part, inside a fixed-size frame with
 * `overflow: hidden`, turned by `transform: translate3d()`. **A page is a column. Page numbers are
 * never persisted.**
 *
 * This file owns every read of layout in the reader and every write of the geometry custom
 * properties. Nothing else may call `scrollWidth` or `getClientRects()` on the track — not because
 * it would be wrong, but because §5.3's whole shape ("one forced layout") is only true if the
 * reads happen in one place with no interleaved writes.
 *
 * WHY NOT `scroll-snap` (§5.1, worth restating where the alternative would be written):
 * on iOS, positions inside a horizontally-scrolling container are not deterministic while momentum
 * is live, and rubber-banding leaves a fractional offset that the snap engine resolves
 * *asynchronously*. Fine for a photo carousel; fatal when a page index must be exactly known the
 * instant a cue crosses a boundary. **A `translate3d` is a number we set; a `scrollLeft` is a
 * number the browser negotiates with a physics engine.**
 *
 * Not React. The reader mounts blocks with React and then hands the resulting DOM to this class;
 * measurement is a synchronous imperative act inside `useLayoutEffect`, and expressing it as state
 * would put a render between the layout flush and the transform, which is a visible flash of the
 * wrong page on every font-size change.
 */

export type SpreadPref = "auto" | "single" | "spread";

/** Everything about the page box, in device-independent pixels. All integers except the ratios. */
export interface Geometry {
  /** Frame border-box size — the numbers §5.1's spread test is written against. */
  outerW: number;
  outerH: number;
  /** Frame content-box size: outer minus the margins. The track lives in here. */
  availW: number;
  availH: number;
  /** One column. In spread mode this is half the usable width, not the whole of it. */
  pageW: number;
  /** Space between two columns — the fore-edge of one page and the spine-edge of the next. */
  gutter: number;
  /** The track's own definite box. `trackW` is `pageW` (single) or `2*pageW + gutter` (spread). */
  trackW: number;
  trackH: number;
  spread: boolean;
  /** How many columns one turn advances. 1 or 2. */
  colsPerTurn: number;
}

export interface GeometryInput {
  /** Frame border-box width in px. */
  outerW: number;
  /** Frame border-box height in px. */
  outerH: number;
  /** Horizontal padding *inside* the frame, both sides summed. */
  padX: number;
  /** Vertical padding inside the frame, both sides summed. */
  padY: number;
  /** Width of one `ch` at the current reading size, measured (never assumed). */
  chPx: number;
  /** Width of one `em` at the current reading size, for the `--read-measure-max: 40em` cap. */
  emPx: number;
  /** Column gap in px. */
  gutter: number;
  /** `--read-measure` in ch. 66 in books' lifted reader.css. */
  measureCh: number;
  /** `--read-measure-max` in em. 40 — past this a column becomes a spread, never a wider column. */
  measureMaxEm: number;
  spreadPref: SpreadPref;
}

/**
 * Spread mode, §5.1: "automatic when `frameW ≥ 1100 && frameW/frameH ≥ 1.2`, user-overridable.
 * This is the MacBook case Karti named and no subsystem shipped it; a 66ch column floating in a
 * 1440px window with grey on both sides is what every mediocre web reader does."
 *
 * The ratio term is what keeps a 1200×1600 tablet in portrait out of spread mode: it is wide
 * enough by pixels and completely wrong for two pages.
 */
export function spreadFor(outerW: number, outerH: number, pref: SpreadPref): boolean {
  if (pref === "single") return false;
  if (pref === "spread") return true;
  return outerW >= 1100 && outerH > 0 && outerW / outerH >= 1.2;
}

/** The narrowest column we will ever set. Below this, two pages are worse than one. */
const MIN_SPREAD_COL_PX = 320;

export function geometryFor(i: GeometryInput): Geometry {
  const outerW = Math.max(0, Math.round(i.outerW));
  const outerH = Math.max(0, Math.round(i.outerH));
  const availW = Math.max(1, Math.round(outerW - i.padX));
  const availH = Math.max(1, Math.round(outerH - i.padY));
  const gutter = Math.max(0, Math.round(i.gutter));

  // The typographic ideal: 66 characters, capped at 40em. §5.8 — "measure caps at 40em and then
  // goes to a spread rather than a wider column". The cap is in em and the target is in ch because
  // they answer different questions: `ch` is "how many characters fit", `em` is "how big is the
  // type"; a face with a narrow zero (EB Garamond) would otherwise get a physically wider column
  // for the same nominal measure.
  const idealCol = Math.min(i.measureCh * i.chPx, i.measureMaxEm * i.emPx);

  let spread = spreadFor(outerW, outerH, i.spreadPref);
  let pageW: number;

  if (spread) {
    const half = Math.floor((availW - gutter) / 2);
    if (half < MIN_SPREAD_COL_PX) {
      // The user asked for a spread on a window too narrow to give it. Refusing is better than
      // two 240px columns; the preference is remembered and takes effect when the window grows.
      spread = false;
      pageW = Math.min(availW, Math.round(idealCol));
    } else {
      pageW = Math.min(half, Math.round(idealCol));
    }
  } else {
    pageW = Math.min(availW, Math.round(idealCol));
  }

  pageW = Math.max(1, Math.round(pageW));
  const trackW = spread ? pageW * 2 + gutter : pageW;

  return {
    outerW,
    outerH,
    availW,
    availH,
    pageW,
    gutter,
    trackW,
    trackH: availH,
    spread,
    colsPerTurn: spread ? 2 : 1,
  };
}

/**
 * Column count from one `scrollWidth` read.
 *
 * §5.3 writes `Math.round(scrollWidth / (pageW + gutter))`, which rounds correctly for every real
 * case but is off by a fraction of a column because the *last* column carries no trailing gap.
 * Adding one gutter back makes it exact arithmetic rather than arithmetic that happens to round
 * the right way: `n` columns occupy `n*pageW + (n-1)*gutter`, so `(that + gutter) / (pageW+gutter)`
 * is exactly `n`. The difference matters at n=1 on a wide column, where the design's form gives
 * `pageW/(pageW+gutter)` — 0.9 for a 380px column and a 40px gutter — and rounds to 1 only because
 * 0.9 > 0.5. Same answer, no cliff.
 */
export function pageCountFrom(scrollWidth: number, pageW: number, gutter: number): number {
  const pitch = pageW + gutter;
  if (!(pitch > 0) || !Number.isFinite(scrollWidth)) return 1;
  return Math.max(1, Math.round((scrollWidth + gutter) / pitch));
}

/**
 * Which column a point at inline offset `x` (relative to the track's content origin) falls in.
 *
 * `floor` and not `round`, because `x` is a point *inside* a column, not the column's edge — a
 * sentence starting 300px into a 380px column must not round up into the next one. The half-gutter
 * bias absorbs the sub-pixel negatives that a fractional `getClientRects().left` produces at a
 * column's left edge, which would otherwise floor to the previous column and pin the reader one
 * page back on every relayout.
 */
export function columnIndexFrom(x: number, pageW: number, gutter: number): number {
  const pitch = pageW + gutter;
  if (!(pitch > 0)) return 0;
  return Math.max(0, Math.floor((x + gutter / 2) / pitch));
}

export interface MeasureResult {
  pageCount: number;
  /** sid → column index. The only map that matters; everything else is derived from it. */
  columnOf: ReadonlyMap<string, number>;
  /** column index → the first sid in it. Forward-filled, so every column has an answer. */
  columnStarts: readonly string[];
  /** Wall-clock cost of the forced layout + rect walk. This is what the §5.3 beacon reports. */
  ms: number;
  /** Sentences the walk found. A zero here with blocks mounted is a renderBlock bug, not a CSS one. */
  sentenceCount: number;
}

const EMPTY_MEASURE: MeasureResult = {
  pageCount: 1,
  columnOf: new Map(),
  columnStarts: [],
  ms: 0,
  sentenceCount: 0,
};

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

export class Paginator {
  readonly frame: HTMLElement;
  readonly track: HTMLElement;

  geometry: Geometry | null = null;
  pageCount = 1;
  /** Current column index. In spread mode this is the LEFT column and is always even. */
  page = 0;
  columnOf: ReadonlyMap<string, number> = new Map();
  columnStarts: readonly string[] = [];

  private willChangeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onTransitionEnd: (e: TransitionEvent) => void;
  private reducedMotion: MediaQueryList | null = null;

  constructor(frame: HTMLElement, track: HTMLElement) {
    this.frame = frame;
    this.track = track;

    // §5.1: "`will-change: transform` is set on gesture/turn start and REMOVED on `transitionend`
    // — a permanently promoted 40-column texture at 3× DPR on a phone is a real memory problem."
    // A 40-column track at 390×844 CSS px is ~15,600×844 device px at 3×; that is a 158 MB layer.
    // Safari does not report this, it just stops compositing and the turn goes to 15 fps.
    this.onTransitionEnd = (e: TransitionEvent) => {
      if (e.target === this.track && e.propertyName === "transform") this.clearWillChange();
    };
    this.track.addEventListener("transitionend", this.onTransitionEnd);

    if (typeof matchMedia === "function") {
      this.reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
    }
  }

  /** Writes the geometry custom properties. The only place they are set. */
  applyGeometry(g: Geometry): void {
    this.geometry = g;
    const f = this.frame.style;
    // Every one of these is a definite pixel length, deliberately. §5.2 pitfall (2): WebKit has
    // historically miscomputed `scrollWidth` when a multicol container's height resolves through a
    // percentage chain, silently producing the wrong page count. `100dvh` would have been correct
    // for *sizing* and wrong for *pagination*, and the failure has no error attached to it.
    // `--frame-h` is the frame's BORDER box (it is what `.pf-frame { height }` consumes, and the
    // frame is `box-sizing: border-box`); `--track-h` is the content box the multicol actually
    // fragments into. Writing one value into both is a real bug and an easy one: the track would
    // be `padY` too tall, overflow the frame it is clipped by, and lose the last line of every
    // column with no error and no visible cause.
    f.setProperty("--frame-w", `${g.availW}px`);
    f.setProperty("--frame-h", `${g.outerH}px`);
    f.setProperty("--page-w", `${g.pageW}px`);
    f.setProperty("--gutter", `${g.gutter}px`);
    f.setProperty("--track-w", `${g.trackW}px`);
    f.setProperty("--track-h", `${g.trackH}px`);
    f.setProperty("--cols-per-turn", String(g.colsPerTurn));
    this.frame.dataset.spread = g.spread ? "1" : "0";
  }

  /**
   * ONE forced layout, then a pure read pass. §5.3's `pageCount` and `columnOf` in one call.
   *
   * Reading `scrollWidth` flushes style and layout for the whole track — that is the expensive
   * thing and it is the number the telemetry beacon exists to measure on a real phone. Every
   * `getClientRects()` after it is served from the same clean layout, so the walk is linear and
   * cheap. **Do not write a style between the two halves of this method**; that reintroduces
   * layout thrashing at one flush per sentence, which is the difference between 40 ms and 4 s.
   */
  measure(): MeasureResult {
    const g = this.geometry;
    if (!g) return EMPTY_MEASURE;

    const t0 = performance.now();

    const scrollWidth = this.track.scrollWidth; // ← the one forced layout
    const pageCount = pageCountFrom(scrollWidth, g.pageW, g.gutter);

    // The track is transform-translated, and so is every descendant, by exactly the same amount.
    // Taking the difference of two rects cancels it, which is why this needs no knowledge of the
    // current page and cannot drift as the reader turns.
    const trackLeft = this.track.getBoundingClientRect().left;

    const columnOf = new Map<string, number>();
    const columnStarts: string[] = new Array(pageCount);
    const nodes = this.track.querySelectorAll<HTMLElement>("[data-s]");

    for (const el of nodes) {
      const sid = el.dataset.s;
      if (!sid) continue;
      // §5.5: "A sentence straddling a column turns at its *first* client rect and stays." The
      // first rect is the first fragment, which is the one the reader's eye reaches first. Using
      // getBoundingClientRect() here would return the union of both fragments — a box spanning the
      // gutter — and every straddling sentence would be assigned to the wrong column.
      const rects = el.getClientRects();
      if (rects.length === 0) continue; // display:none, or a zero-width empty span
      const col = clamp(columnIndexFrom(rects[0]!.left - trackLeft, g.pageW, g.gutter), 0, pageCount - 1);
      columnOf.set(sid, col);
      if (columnStarts[col] === undefined) columnStarts[col] = sid;
    }

    // Forward-fill. A column can legitimately contain no sentence at all — a full-page image, a
    // code block, a stanza ornament — and `sidAtColumn` must still answer, because that answer is
    // the pin that survives the next font-size change.
    let last: string | undefined;
    for (let c = 0; c < pageCount; c++) {
      if (columnStarts[c] === undefined) {
        if (last !== undefined) columnStarts[c] = last;
      } else {
        last = columnStarts[c];
      }
    }

    this.pageCount = pageCount;
    this.columnOf = columnOf;
    this.columnStarts = columnStarts;
    this.page = clamp(this.page, 0, this.maxColumn());

    return { pageCount, columnOf, columnStarts, ms: performance.now() - t0, sentenceCount: columnOf.size };
  }

  /** The highest column a turn may land on. In spread mode always even. */
  maxColumn(): number {
    return this.snap(Math.max(0, this.pageCount - 1));
  }

  /** Align a column index to a turn boundary. Identity in single mode. */
  snap(col: number): number {
    const per = this.geometry?.colsPerTurn ?? 1;
    if (per <= 1) return Math.max(0, Math.floor(col));
    return Math.max(0, Math.floor(col / per) * per);
  }

  /** Turns, not columns — what "page 7 of 21" counts. */
  turnCount(): number {
    const per = this.geometry?.colsPerTurn ?? 1;
    return Math.max(1, Math.ceil(this.pageCount / per));
  }

  turnIndex(): number {
    const per = this.geometry?.colsPerTurn ?? 1;
    return Math.floor(this.page / per);
  }

  columnForSid(sid: string): number | undefined {
    return this.columnOf.get(sid);
  }

  /** The first sid visible on the given column, forward-filled. */
  sidAtColumn(col: number): string | undefined {
    return this.columnStarts[clamp(col, 0, this.pageCount - 1)];
  }

  /**
   * The pin. §5.3 step 1 — "PIN FIRST". In spread mode this is the left column's first sentence,
   * because that is where the eye is and because pinning the right-hand page would move the reader
   * back one page every time a relayout turned a spread into a single column.
   */
  topVisibleSid(): string | undefined {
    return this.sidAtColumn(this.page);
  }

  /**
   * Move to a column. Returns the column actually landed on (clamped and turn-aligned).
   *
   * `animate: false` is not "skip the transition" — it is "make this frame show the new page with
   * no interpolation at all", which is what repaginate() needs after a font-size change and what
   * the §5.7 visibility resync needs ("one synchronous resync before paint. No catching-up
   * animation."). Setting `--page` while a 220 ms transition is armed would slide the reader
   * across nine columns of a book they cannot read.
   */
  goTo(col: number, opts: { animate?: boolean } = {}): number {
    const target = clamp(this.snap(col), 0, this.maxColumn());
    const animate = opts.animate !== false;

    if (!animate) {
      const prev = this.track.style.transition;
      this.track.style.transition = "none";
      this.track.style.setProperty("--page", String(target));
      // Force the style change to be committed with the transition still suppressed. Reading
      // offsetWidth is the cheapest way to flush; without it the two writes coalesce in the same
      // task and the browser interpolates from the old value anyway.
      void this.track.offsetWidth;
      this.track.style.transition = prev;
      this.clearWillChange();
    } else {
      this.setWillChange();
      this.track.style.setProperty("--page", String(target));
      this.fadeIfReducedMotion();
    }

    this.page = target;
    return target;
  }

  /** Advance by `delta` turns (not columns). Returns the column landed on. */
  turn(delta: number, opts: { animate?: boolean } = {}): number {
    const per = this.geometry?.colsPerTurn ?? 1;
    return this.goTo(this.page + delta * per, opts);
  }

  /** True when a turn in this direction would leave the current chapter part. */
  atStart(): boolean {
    return this.page <= 0;
  }
  atEnd(): boolean {
    return this.page >= this.maxColumn();
  }

  /**
   * Drag support. During a swipe the track follows the finger, so the transform cannot come from
   * `--page` alone; `--drag` is added to it in the CSS calc and is always 0 except mid-gesture.
   */
  setDragOffset(px: number): void {
    this.track.style.setProperty("--drag", `${px}px`);
  }
  clearDragOffset(): void {
    this.track.style.setProperty("--drag", "0px");
  }
  /** Turn the CSS transition off for the duration of a finger-tracking drag. */
  setDragging(on: boolean): void {
    this.frame.dataset.dragging = on ? "1" : "0";
    if (on) this.setWillChange();
  }

  setWillChange(): void {
    this.track.style.willChange = "transform";
    if (this.willChangeTimer) clearTimeout(this.willChangeTimer);
    // `transitionend` does not fire when the transform does not actually change — a turn against
    // the first or last page, or a drag that snaps back to where it started. Without this the
    // layer stays promoted forever, which is precisely the memory problem the removal exists to
    // prevent, arrived at by a different road.
    this.willChangeTimer = setTimeout(() => this.clearWillChange(), 700);
  }

  clearWillChange(): void {
    if (this.willChangeTimer) {
      clearTimeout(this.willChangeTimer);
      this.willChangeTimer = null;
    }
    this.track.style.willChange = "";
  }

  /**
   * §5.1: "@media (prefers-reduced-motion: reduce) { .pf-track { transition: none; } } → 110ms
   * cross-fade". The transition is killed in CSS; the fade has to be driven from here because it
   * is a one-shot on the *new* content, not a state. Restarting a CSS animation requires the
   * none/reflow/set dance — assigning the same animation value twice is a no-op.
   */
  private fadeIfReducedMotion(): void {
    if (!this.reducedMotion?.matches) return;
    this.track.style.animation = "none";
    void this.track.offsetWidth;
    this.track.style.animation = "ab-page-fade 110ms linear";
  }

  destroy(): void {
    this.track.removeEventListener("transitionend", this.onTransitionEnd);
    this.clearWillChange();
  }
}
